import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  CreateUploadDto,
  DefaultApiUploadsControllerUpdateRequest,
  UploadWithAssetsDto,
} from "generated";
import { useParams } from "react-router";
import api from "../services/api/axios-client";
import { ValidationErrorResponse, ValidationField } from "./types";
import { useStore } from "@/services/store";
import { ValidationEntry } from "@/services/store/slices/mandatoryMetaData/types";
import { useShallow } from "zustand/react/shallow";

export const useCurrentUploadId = () => {
  const { uploadId } = useParams();
  if (!uploadId) {
    throw new Error("Missing expected upload ID");
  }
  return Number(uploadId);
};

export const useOptionalUploadId = () => {
  const { uploadId } = useParams();
  return Number(uploadId);
};

export const useCurrentUploadQuery = () => {
  const params = useParams();
  const {
    initializeMetadata,
    initializeVideoMetadata,
    initializeDocumentMetadata,
  } = useStore(
    useShallow((state) => ({
      initializeMetadata: state.initializeMetadata,
      initializeVideoMetadata: state.initializeVideoMetadata,
      initializeDocumentMetadata: state.initializeDocumentMetadata,
    })),
  );
  const uploadId = useOptionalUploadId();
  return useQuery({
    queryKey: ["uploads", uploadId],
    queryFn: async () => {
      return api
        .uploadsControllerFindOne({ id: Number(uploadId) })
        .then((response) => {
          if (response.status === 200) {
            initializeVideoMetadata(response.data);
            initializeDocumentMetadata(response.data, params.assetIds);
            initializeMetadata(response.data, params.assetIds);
          }
          return response.data;
        });
    },
  });
};

export const useCreateUploadMutation = () => {
  return useMutation({
    mutationFn: async (newUpload: CreateUploadDto) => {
      return api.uploadsControllerCreate({ createUploadDto: newUpload });
    },
  });
};

export const useUploadsQuery = () => {
  return useQuery({
    queryKey: ["uploads"],
    queryFn: async () => api.uploadsControllerFindAll(),
  });
};

export const useUploadUpdateMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updateBody: DefaultApiUploadsControllerUpdateRequest) => {
      return api.uploadsControllerUpdate(updateBody);
    },
    onSuccess(responseData) {
      const upload = responseData.data;
      queryClient.setQueryData(
        ["uploads", upload.id],
        (cachedData: UploadWithAssetsDto) => {
          return {
            ...cachedData,
            ...upload,
          };
        },
      );
    },
  });
};

export const useUploadValidationQuery = (uploadId: number) => {
  const setValidationErrors = useStore((state) => state.setValidationError);
  return useQuery<
    boolean,
    AxiosError<{
      message: ValidationEntry[];
    }>
  >({
    enabled: false,
    retry: false,
    staleTime: 0,
    queryKey: ["metadata-validation", uploadId],
    queryFn: async () => {
      try {
        await api.uploadsControllerValidateUploadAssets({ id: uploadId });
        return true;
      } catch (error) {
        if (error instanceof AxiosError && error?.response?.status === 400) {
          setValidationErrors(error.response.data.message);
        }
        return false;
      }
    },
  });
};

export const useUploadValidateDocument = (uploadId: number) => {
  const setValidation = useStore(
    (state) => state.setDocumentMetadataValidation,
  );
  return useQuery<boolean, AxiosError<ValidationErrorResponse>>({
    enabled: false,
    retry: false,
    staleTime: 0,
    queryKey: ["document-validation", uploadId],
    queryFn: async () => {
      try {
        await api.uploadsControllerValidateDocumentUpload({ uploadId });
        return true;
      } catch (error) {
        if (error instanceof AxiosError && error?.response?.status === 400) {
          const validationErrors: ValidationErrorResponse = error.response.data;

          const errors = validationErrors.message.reduce(
            (acc, { assetId, field, message }) => {
              if (!acc[assetId]) {
                acc[assetId] = [];
              }
              acc[assetId].push({ field, message });
              return acc;
            },
            {} as Record<number, ValidationField[]>,
          );

          Object.entries(errors).forEach(([key, value]) => {
            setValidation(Number(key), value);
          });
        }
        return false;
      }
    },
  });
};

export const useUploadValidateMotionMedia = (uploadId: number) => {
  const setValidation = useStore((state) => state.setVideoMetadataValidation);
  return useQuery<boolean, AxiosError<ValidationErrorResponse>>({
    enabled: false,
    retry: false,
    staleTime: 0,
    queryKey: ["motion-media-validation", uploadId],
    queryFn: async () => {
      try {
        await api.uploadsControllerValidateMotionMediaUpload({
          uploadId,
        });
        return true;
      } catch (error) {
        if (error instanceof AxiosError && error?.response?.status === 400) {
          const validationErrors: ValidationErrorResponse = error.response.data;

          const errors = validationErrors.message.reduce(
            (acc, { assetId, field, message }) => {
              if (!acc[assetId]) {
                acc[assetId] = [];
              }
              acc[assetId].push({ field, message });
              return acc;
            },
            {} as Record<number, ValidationField[]>,
          );

          Object.entries(errors).forEach(([key, value]) => {
            setValidation(Number(key), value);
          });
        }
        return false;
      }
    },
  });
};

export const useRemoveUploadMutation = () => {
  return useMutation({
    mutationFn: (uploadId: number) => {
      return api.uploadsControllerRemove({ id: uploadId });
    },
  });
};
